import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Square, Timer } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { useEnvStore } from "@/os/env/useEnvStore";
import { useI18n } from "@/i18n/I18nProvider";
import {
  getActiveTimer,
  stopTimer,
  fmtDurationClock,
  TIMER_CHANGED_EVENT,
  type TimeEntry,
} from "@/os/data/timeTrackingApi";
import {
  getActiveTimerMock,
  stopTimerMock,
  getTimerMetaMock,
} from "@/os/data/mock/timeTrackingMock";
import { osToast } from "@/os/toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { pickLang } from "@/os/utils/lang";

interface Meta { projectName: string | null; taskTitle: string | null }

/**
 * Sidebar widget that shows the currently running time-tracking timer
 * across every OS page and lets the operator stop it without going back
 * to the Work Studio.
 */
export function GlobalTimerIndicator({ collapsed }: { collapsed: boolean }) {
  const { user } = useAuth();
  const env = useEnvStore((s) => s.env);
  const isSandbox = env === "sandbox";
  const { t, lang } = useI18n();
  const nav = useNavigate();

  const [entry, setEntry] = useState<TimeEntry | null>(null);
  const [meta, setMeta] = useState<Meta>({ projectName: null, taskTitle: null });
  const [now, setNow] = useState(() => Date.now());
  const [stopping, setStopping] = useState(false);

  const refresh = useCallback(async () => {
    if (!user?.id) { setEntry(null); return; }
    if (isSandbox) {
      const e = getActiveTimerMock(user.id);
      setEntry(e);
      setMeta(e ? getTimerMetaMock(e) : { projectName: null, taskTitle: null });
      return;
    }
    try {
      const e = await getActiveTimer(user.id);
      setEntry(e);
      if (e) {
        const [{ data: p }, { data: tk }] = await Promise.all([
          supabase.from("projects").select("name").eq("id", e.project_id).maybeSingle(),
          e.task_id
            ? supabase.from("tasks" as any).select("title").eq("id", e.task_id).maybeSingle()
            : Promise.resolve({ data: null } as any),
        ]);
        setMeta({
          projectName: (p as any)?.name ?? null,
          taskTitle: (tk as any)?.title ?? null,
        });
      }
    } catch {
      /* silent */
    }
  }, [isSandbox, user?.id]);

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    window.addEventListener(TIMER_CHANGED_EVENT, onChange);
    // Light polling fallback (covers other tabs / external edits).
    const poll = window.setInterval(refresh, 30_000);
    return () => {
      window.removeEventListener(TIMER_CHANGED_EVENT, onChange);
      window.clearInterval(poll);
    };
  }, [refresh]);

  // Tick the visible clock every second while a timer is active.
  useEffect(() => {
    if (!entry) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [entry]);

  if (!entry) return null;

  const elapsed = Math.max(0, Math.floor((now - new Date(entry.started_at).getTime()) / 1000));
  const clock = fmtDurationClock(elapsed);
  const label = meta.taskTitle || meta.projectName || (pickLang(lang, "Sesión activa", "Active session"));
  const sub = meta.taskTitle && meta.projectName ? meta.projectName : null;

  const handleStop = async (ev: React.MouseEvent) => {
    ev.stopPropagation();
    ev.preventDefault();
    if (stopping) return;
    setStopping(true);
    try {
      if (isSandbox) stopTimerMock(entry.id);
      else await stopTimer(entry.id);
      setEntry(null);
      osToast.notify({
        en: { title: "Timer stopped", description: `Logged ${clock} to your timesheet.` },
        es: { title: "Timer detenido", description: `Registramos ${clock} en tu hoja de tiempo.` },
      });
    } catch (e: any) {
      osToast.failed(e);
    } finally {
      setStopping(false);
    }
  };

  const go = () => nav("/os/operator/work-studio");

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={go}
            className="relative mx-auto flex h-9 w-9 items-center justify-center rounded-md border border-rose-500/40 bg-rose-500/[0.08] text-rose-500 hover:bg-rose-500/[0.14] transition-colors"
            aria-label={`${label} · ${clock}`}
          >
            <Timer className="h-4 w-4" />
            <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-rose-500 animate-pulse" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-[220px]">
          <div className="flex flex-col gap-0.5">
            <span className="font-mono text-[11px] tabular-nums text-rose-500">{clock}</span>
            <span className="text-[11px] truncate">{label}</span>
            {sub && <span className="text-[10px] text-muted-foreground truncate">{sub}</span>}
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <button
      onClick={go}
      className={cn(
        "group flex w-full items-center gap-2 rounded-md border border-rose-500/30 bg-rose-500/[0.06] px-2 py-1.5 text-left transition-colors",
        "hover:bg-rose-500/[0.10]",
      )}
      aria-label={`${label} · ${clock}`}
    >
      <span className="relative inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-rose-500/15 text-rose-500">
        <Timer className="h-3.5 w-3.5" />
        <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-rose-500 animate-pulse" />
      </span>
      <div className="min-w-0 flex-1 leading-tight">
        <div className="font-mono text-[11px] tabular-nums text-rose-500">{clock}</div>
        <div className="truncate text-[11px] text-foreground">{label}</div>
        {sub && <div className="truncate text-[10px] text-muted-foreground">{sub}</div>}
      </div>
      <span
        role="button"
        tabIndex={0}
        onClick={handleStop}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleStop(e as any); }}
        title={pickLang(lang, "Detener", "Stop")}
        className={cn(
          "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-rose-500/40 text-rose-500 transition-colors",
          "hover:bg-rose-500 hover:text-white",
          stopping && "opacity-50 pointer-events-none",
        )}
      >
        <Square className="h-3 w-3 fill-current" />
      </span>
    </button>
  );
}
